<script>
import api from '@utils/api'
import userHasRolesToWrite from '@utils/userPermissions'

export default {
  name: 'GoodsDrumvolume',
  props: {
    drumvolume: {
      type: Object,
      default: () => {
        return {}
      },
    },
  },
  data() {
    return {
      loading: false,
      valid: true,
      item: {
        h: null,
        s: null,
        w: null,
        d: null,
        indent: null,
        volume: null,
        volumeUnit: 103,
        type: null,
      },
      rolesAllowedToWrite: ['SUPERUSER', 'DICTIONARIES_OPERATOR'],
    }
  },
  computed: {
    volumeUnit() {
      return this.$store.getters['data/getDictionary']('unit')
    },
    unitsMeasurement() {
      return this.$store.getters['userSettings/getUnitsMeasurement']
    },
    userAllowedToUpdate() {
      return userHasRolesToWrite(
        this.$store.state.auth.userRole,
        this.rolesAllowedToWrite
      )
    },
  },
  watch: {
    drumvolume: {
      handler(value) {
        this.item.h = this.modifyData(value.h, false) || null
        this.item.s = this.modifyData(value.s, false) || null
        this.item.indent = this.modifyData(value.indent, false) || null
        this.item.volume = this.modifyData(value.volume, true) || null
        this.item.volumeUnit = value.volumeUnit || 103
        this.item.type = value.type || 'cylindrical'
      },
      deep: true,
    },
  },
  created() {
    Object.assign(this.item, {
      h: this.modifyData(this.drumvolume.h, false),
      s: this.modifyData(this.drumvolume.s, false),
      w: this.modifyData(this.drumvolume.w, false),
      d: this.modifyData(this.drumvolume.d, false),
      indent: this.modifyData(this.drumvolume.indent, false),
      volume: this.modifyData(this.drumvolume.volume, true),
      volumeUnit: this.drumvolume.volumeUnit || 103,
      type: this.drumvolume.type || null,
      picked: this.drumvolume.type === 'cylindrical',
    })
  },
  methods: {
    setParameterValue(param, multiplier) {
      if (param) {
        return parseFloat(param.toString().replace(',', '.')) * multiplier
      }
    },
    createDrumvolume() {
      this.loading = true
      let data = {}
      if (this.cylindrical()) {
        data = {
          h: this.setParameterValue(
            this.item.h,
            this.unitsMeasurement.lengthMultiplier
          ),
          s: this.setParameterValue(
            this.item.s,
            this.unitsMeasurement.lengthMultiplier
          ),
          indent: this.setParameterValue(
            this.item.indent,
            this.unitsMeasurement.lengthMultiplier
          ),
          volume: this.setParameterValue(
            this.item.volume,
            this.unitsMeasurement.volumeMultiplier
          ),
          volumeUnit: this.item.volumeUnit,
          type: this.item.type,
        }
        this.item.d = null
        this.item.w = null
      } else {
        data = {
          h: this.setParameterValue(
            this.item.h,
            this.unitsMeasurement.lengthMultiplier
          ),
          w: this.setParameterValue(
            this.item.w,
            this.unitsMeasurement.lengthMultiplier
          ),
          d: this.setParameterValue(
            this.item.d,
            this.unitsMeasurement.lengthMultiplier
          ),
          volume: this.setParameterValue(
            this.item.volume,
            this.unitsMeasurement.volumeMultiplier
          ),
          volumeUnit: this.item.volumeUnit,
          type: this.item.type,
        }
        this.item.s = null
        this.item.indent = null
      }
      api
        .createGoodsDrumvolume({
          goodsId: this.$route.params.goodsId,
          data: data,
        })
        .then(response => {
          this.loading = false
          this.$emit('drumvolume-updated')
        })
    },
    modifyData(value, volume) {
      if (value) {
        if (volume) {
          return value / this.unitsMeasurement.volumeMultiplier
        } else {
          return value / this.unitsMeasurement.lengthMultiplier
        }
      }
    },
    getSymbol(code) {
      switch (code) {
        case 'liters':
          return 'L'
        case 'us_gallon':
        case 'imperial_gallon':
          return 'gal'
        case 'millimeters':
          return 'mm'
        case 'inches':
          return '"'
        default:
          return ''
      }
    },
    getValueInMeasureUnits(volume) {
      if (volume) return this.unitsMeasurement.volumeMeasure
      else return this.unitsMeasurement.lengthMeasure
    },
    cylindrical() {
      return this.item.type === 'cylindrical'
    },
    cubical() {
      return this.item.type === 'cubical'
    },
  },
}
</script>

<template>
  <v-container fluid>
    <v-layout wrap>
      <v-flex lg4>
        <v-form
          ref="drumoilForm"
          v-model="valid"
          lazy-validation>
          <div class="pb-4">{{ $t(`label__drum_shape`)+':' }}</div>
          <div class="pl-4">
            <input
              id="cylindrical"
              v-model="item.type"
              value="cylindrical"
              name="drumType"
              type="radio">
            <label
              for="cylindrical"
              class="pl-2"> {{ $t(`label__cylindrical`) }}</label>
            <div class="pl-5 pt-3">
              <div v-if="cylindrical()">
                <v-text-field
                  :label="
                    `${$t('label__height')} (${$t(
                      `label__${getValueInMeasureUnits(false)}`
                    )})`
                  "
                  :readonly="!userAllowedToUpdate"
                  v-model="item.h"
                  name="h"
                  autocomplete="off"
                />
                <v-text-field
                  :label="
                    `${$t('label__diameter')} (${$t(
                      `label__${getValueInMeasureUnits(false)}`
                    )})`
                  "
                  :readonly="!userAllowedToUpdate"
                  v-model="item.s"
                  name="s"
                  autocomplete="off"
                />
                <v-text-field
                  :label="
                    `${$t('label__indent')} (${$t(
                      `label__${getValueInMeasureUnits(false)}`
                    )})`
                  "
                  :readonly="!userAllowedToUpdate"
                  v-model="item.indent"
                  name="indent"
                  autocomplete="off"
                />
              </div>
              <div v-if="!cylindrical()">
                <div class="pb-3">{{ `${$t('label__height')} (${$t(`label__${getValueInMeasureUnits(false)}`)})` }}</div>
                <div class="pb-3">{{ `${$t('label__diameter')} (${$t(`label__${getValueInMeasureUnits(false)}`)})` }}</div>
                <div class="pb-3">{{ `${$t('label__indent')} (${$t(`label__${getValueInMeasureUnits(false)}`)})` }}</div>
              </div>
            </div>
          </div>
          <div class="pl-4">
            <input
              id="cubical"
              v-model="item.type"
              value="cubical"
              type="radio"
              name="drumType">
            <label
              for="cubical"
              class="pl-2"> {{ $t(`label__cubical`) }}</label>
            <div class="pl-5 pt-3">
              <div v-if="cubical()">
                <v-text-field
                  :label="
                    `${$t('label__height')} (${$t(
                      `label__${getValueInMeasureUnits(false)}`
                    )})`
                  "
                  :readonly="!userAllowedToUpdate"
                  v-model="item.h"
                  name="h"
                  autocomplete="off"
                />
                <v-text-field
                  :label="
                    `${$t('label__width')} (${$t(
                      `label__${getValueInMeasureUnits(false)}`
                    )})`
                  "
                  :readonly="!userAllowedToUpdate"
                  v-model="item.w"
                  name="s"
                  autocomplete="off"
                />
                <v-text-field
                  :label="
                    `${$t('label__depth')} (${$t(
                      `label__${getValueInMeasureUnits(false)}`
                    )})`
                  "
                  :readonly="!userAllowedToUpdate"
                  v-model="item.d"
                  name="indent"
                  autocomplete="off"
                />
              </div>
              <div v-if="!cubical()">
                <div class="pb-3">{{ `${$t('label__height')} (${$t(`label__${getValueInMeasureUnits(false)}`)})` }}</div>
                <div class="pb-3">{{ `${$t('label__width')} (${$t(`label__${getValueInMeasureUnits(false)}`)})` }}</div>
                <div class="pb-3">{{ `${$t('label__depth')} (${$t(`label__${getValueInMeasureUnits(false)}`)})` }}</div>
              </div>
            </div>
          </div>
          <v-text-field
            :label="
              `${$t('label__volume')} (${$t(
                `label__${getValueInMeasureUnits(true)}`
              )})`
            "
            :readonly="!userAllowedToUpdate"
            v-model="item.volume"
            name="volume"
            autocomplete="off"
          />
          <!-- <v-select
            :items="volumeUnit"
            :label="$t('label__volume_unit')"
            v-model="item.volumeUnit"
            item-text="name"
            item-value="code"
            autocomplete
            readonly
          /> -->
        </v-form>
        <div :class="$style.btnWrapper">
          <v-btn
            v-if="userAllowedToUpdate"
            :loading="loading"
            :disabled="loading"
            color="success"
            @click="createDrumvolume"
          >
            {{ $t('buttons__update') }}
          </v-btn>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<style lang="scss" module>
@import '@design';
.btnWrapper {
  margin: 10px -7px;
}
</style>
